import React from 'react';

const RESPONSES = ['YES', 'NO', 'MAYBE'];

const RESPONSE_STYLES = {
  YES:   { active: 'bg-green-500 text-white border-green-500',  hover: 'hover:bg-green-50 hover:border-green-400' },
  NO:    { active: 'bg-red-500 text-white border-red-500',      hover: 'hover:bg-red-50 hover:border-red-400'   },
  MAYBE: { active: 'bg-yellow-400 text-white border-yellow-400', hover: 'hover:bg-yellow-50 hover:border-yellow-400' },
};

const RESPONSE_LABELS = { YES: 'Yes', NO: 'No', MAYBE: 'Maybe' };

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// In this system: Thu(4), Fri(5), Sat(6) are weekends
const isWeekend = (date) => [4, 5, 6].includes(date.getDay());

/** Returns all Date objects between start and end inclusive. */
function buildDateRange(start, end) {
  const dates = [];
  const cur = new Date(start + 'T00:00:00');
  const last = new Date(end   + 'T00:00:00');
  while (cur <= last) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/** Group dates into calendar weeks (rows), padding the first and last rows. */
function groupIntoWeeks(dates) {
  if (!dates.length) return [];
  const weeks = [];
  let week = Array(dates[0].getDay()).fill(null); // pad leading empty cells
  for (const d of dates) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

// ── Single day cell ──────────────────────────────────────────────────────────

function DayCell({ date, response, isHeavy, onChange, maxNoWeekday, maxNoWeekend, noWeekdayUsed, noWeekendUsed }) {
  const weekend = isWeekend(date);
  const atNoLimit = weekend
    ? noWeekendUsed >= maxNoWeekend && response !== 'NO'
    : noWeekdayUsed >= maxNoWeekday && response !== 'NO';

  return (
    <div
      className={`rounded-xl border p-2 flex flex-col gap-1.5 min-h-[100px] ${
        isHeavy
          ? 'border-orange-300 bg-orange-50'
          : weekend
          ? 'border-blue-200 bg-blue-50/40'
          : 'border-gray-200 bg-white'
      }`}
    >
      {/* Date header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">
          {date.getDate()}
        </span>
        <div className="flex gap-1">
          {isHeavy && (
            <span className="rounded-full bg-orange-400 px-1.5 py-0.5 text-[10px] font-bold text-white">
              H
            </span>
          )}
          {weekend && (
            <span className="rounded-full bg-blue-400 px-1.5 py-0.5 text-[10px] font-bold text-white">
              E
            </span>
          )}
          {!weekend && (
            <span className="rounded-full bg-gray-400 px-1.5 py-0.5 text-[10px] font-bold text-white">
              D
            </span>
          )}
        </div>
      </div>

      {/* Yes / No / Maybe buttons */}
      <div className="flex flex-col gap-1">
        {RESPONSES.map((r) => {
          const isActive  = response === r;
          const disabled  = r === 'NO' && atNoLimit;
          const styles    = RESPONSE_STYLES[r];
          return (
            <button
              key={r}
              disabled={disabled}
              onClick={() => onChange(date.toISOString().slice(0, 10), isActive ? null : r)}
              className={`
                w-full rounded border text-[11px] font-medium py-0.5 transition-colors
                ${isActive ? styles.active : `border-gray-200 text-gray-500 bg-white ${disabled ? 'opacity-30 cursor-not-allowed' : styles.hover}`}
              `}
            >
              {RESPONSE_LABELS[r]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main grid ────────────────────────────────────────────────────────────────

export default function AvailabilityGrid({
  period,
  heavyDates,
  responses,        // { 'YYYY-MM-DD': 'YES' | 'NO' | 'MAYBE' }
  onChange,         // (date, response | null) => void
}) {
  const dates = buildDateRange(period.start_date, period.end_date);
  const weeks = groupIntoWeeks(dates);
  const heavySet = new Set(heavyDates);

  // Live No counters
  const noWeekdayUsed = dates.filter(
    (d) => !isWeekend(d) && responses[d.toISOString().slice(0, 10)] === 'NO'
  ).length;
  const noWeekendUsed = dates.filter(
    (d) => isWeekend(d) && responses[d.toISOString().slice(0, 10)] === 'NO'
  ).length;

  const maxNoWeekday = period.max_no_weekdays ?? Infinity;
  const maxNoWeekend = period.max_no_weekends ?? Infinity;

  // Count answered days
  const answeredCount = dates.filter(
    (d) => responses[d.toISOString().slice(0, 10)]
  ).length;

  return (
    <div className="space-y-4">

      {/* Counters bar */}
      <div className="flex flex-wrap gap-3">
        <Counter
          label="Weekday No's used"
          used={noWeekdayUsed}
          max={period.max_no_weekdays}
          color="red"
        />
        <Counter
          label="Weekend No's used"
          used={noWeekendUsed}
          max={period.max_no_weekends}
          color="red"
        />
        <Counter
          label="Days answered"
          used={answeredCount}
          max={dates.length}
          color="green"
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-gray-400" /> D = Weekday
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-blue-400" /> E = Weekend (Thu–Sat)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-orange-400" /> H = Heavy Day
        </span>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-2">
        {DAY_NAMES.map((n) => (
          <div key={n} className="text-center text-xs font-semibold text-gray-500 pb-1">
            {n}
          </div>
        ))}

        {/* Calendar cells */}
        {weeks.map((week, wi) =>
          week.map((date, di) =>
            date ? (
              <DayCell
                key={date.toISOString()}
                date={date}
                response={responses[date.toISOString().slice(0, 10)] || null}
                isHeavy={heavySet.has(date.toISOString().slice(0, 10))}
                onChange={onChange}
                maxNoWeekday={maxNoWeekday}
                maxNoWeekend={maxNoWeekend}
                noWeekdayUsed={noWeekdayUsed}
                noWeekendUsed={noWeekendUsed}
              />
            ) : (
              <div key={`empty-${wi}-${di}`} />
            )
          )
        )}
      </div>
    </div>
  );
}

function Counter({ label, used, max, color }) {
  const over = max !== null && used > max;
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
        over ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
      }`}
    >
      <span className="text-gray-600">{label}:</span>
      <span className={`font-bold ${over ? 'text-red-600' : color === 'green' ? 'text-green-600' : 'text-gray-900'}`}>
        {used}
      </span>
      {max !== null && (
        <span className="text-gray-400">/ {max}</span>
      )}
    </div>
  );
}
